/**
 * Deterministisk seedgenerator for demoverdenen.
 * 90 kalenderdager (7. juli – 4. oktober 2026), frosset klokke 4. oktober 08.00 Oslo.
 * Alle tall er fiktive produktvalg for demonstrasjon – ingen reelle driftsdata.
 */
import type {
  ActivityAllocation,
  ContextLoad,
  Dataset,
  DayInfo,
  Fraction,
  LineStateInterval,
  Machine,
  MachineInterval,
  ProductionInterval,
  ProductionLine,
  Rates,
  Site,
  SourceSync,
} from '../domain/types';
import { addDays, eachDay, osloLocalIso, weekdayOf } from '../domain/time';
import { DEMO_NOW_UTC, DEMO_WINDOW, SEED_VERSION, getScenario } from './scenarios';

export { DEMO_NOW_UTC, DEMO_WINDOW, SEED_VERSION };

export const SITE: Site = { siteId: 'dimmelsvik', name: 'Dimmelsvik', timezone: 'Europe/Oslo' };
export const LINE: ProductionLine = { lineId: 'linje-1', siteId: 'dimmelsvik', name: 'Knuseverk linje 1' };
export const FRACTIONS: Fraction[] = [
  { fractionId: 'F1', name: 'Fraksjon A (0–22 mm)', share: 0.6 },
  { fractionId: 'F2', name: 'Fraksjon B (22–120 mm)', share: 0.4 },
];

export const MACHINES: Machine[] = [
  m('DEMO-E01', 'Gravemaskin 1', 'gravemaskin', 'production'),
  m('DEMO-E02', 'Gravemaskin 2', 'gravemaskin', 'production'),
  m('DEMO-D01', 'Dumper 1', 'dumper', 'production'),
  m('DEMO-D02', 'Dumper 2', 'dumper', 'production'),
  m('DEMO-D03', 'Dumper 3', 'dumper', 'production'),
  m('DEMO-L01', 'Hjullaster 1', 'hjullaster', 'production'),
  m('DEMO-L02', 'Hjullaster 2', 'hjullaster', 'production'),
  m('DEMO-L03', 'Hjullaster 3', 'hjullaster', 'production'),
  m('DEMO-L04', 'Hjullaster 4 (utlasting)', 'hjullaster', 'loading_out'),
  m('DEMO-S01', 'Servicebil 1', 'servicebil', 'service'),
];

function m(id: string, name: string, type: Machine['type'], activity: Machine['defaultActivity']): Machine {
  return {
    machineId: id,
    siteId: SITE.siteId,
    fictionalName: name,
    type,
    activeFrom: '2026-01-01T00:00:00.000Z',
    activeTo: null,
    defaultActivity: activity,
    inProductionScope: activity === 'production',
  };
}

export const SCOPE_MACHINE_IDS = MACHINES.filter((x) => x.inProductionScope).map((x) => x.machineId);

export const DEFAULT_RATES: Rates = {
  dieselNokPerLitre: 20,
  machineNokPerHour: 400,
  co2KgPerLitre: 2.68,
  co2FactorUnit: 'kg CO₂/L',
  co2FactorSource: 'Forutsetning i eksempeldatasettet – generisk faktor for anleggsdiesel. Ikke verifisert lokal faktor; må bekreftes mot leverandør/dokumentasjon før produksjonsbruk.',
  co2FactorMethod: 'Estimat: diesel (L) × faktor. Kun direkte forbrenning (tank-til-hjul). Ingen livsløps- eller CO₂e-påstand.',
  currency: 'NOK',
  effectiveFrom: '2026-01-01',
  effectiveTo: null,
  provenance: 'Antatt sats (ikke kundens sats)',
  isIllustrative: true,
};

export const SHIFT_START_HOUR = 7;
export const SHIFT_HOURS = 9;

/* ---------- deterministisk PRNG ---------- */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function roundTo(v: number, step: number): number {
  return Math.round(v / step) * step;
}

/** Deler total etter vekter med 4 desimaler; resten legges på siste del slik at summen er eksakt. */
export function splitExact(total: number, weights: number[], decimals = 4): number[] {
  const w = weights.reduce((a, b) => a + b, 0);
  const f = 10 ** decimals;
  const parts: number[] = [];
  let acc = 0;
  for (let i = 0; i < weights.length; i++) {
    if (i === weights.length - 1) {
      parts.push(Math.round((total - acc) * f) / f);
    } else {
      const p = w > 0 ? Math.round(((total * weights[i]) / w) * f) / f : 0;
      parts.push(p);
      acc += p;
    }
  }
  return parts;
}

/* ---------- dagsspesifikasjoner ---------- */
interface Segment {
  state: 'production' | 'stop';
  fromH: number;
  toH: number;
  stopReason?: string;
}

interface MachineDaySpec {
  engine: number;
  idle: number;
  fuel: number;
  /** vekt per timeslot (9 slots) for tomgang; standard jevn fordeling */
  idleWeights?: number[];
}

interface DaySpec {
  date: string;
  kind: DayInfo['kind'];
  note?: string;
  tonnes: number;
  segments: Segment[];
  machines: Record<string, MachineDaySpec>;
  nonScope: Record<string, MachineDaySpec>;
  loads: { båt: number; bil: number };
}

const EVEN = [1, 1, 1, 1, 1, 1, 1, 1, 1];

/** Fast testgrunnlag 1. oktober (normal). */
const FIXED_2026_10_01: DaySpec = {
  date: '2026-10-01',
  kind: 'workday',
  tonnes: 4000,
  segments: [
    { state: 'production', fromH: 7, toH: 11 },
    { state: 'stop', fromH: 11, toH: 12, stopReason: 'Planlagt pause og rutinekontroll' },
    { state: 'production', fromH: 12, toH: 16 },
  ],
  machines: {
    'DEMO-E01': { engine: 7, idle: 1.0, fuel: 190 },
    'DEMO-E02': { engine: 7, idle: 1.0, fuel: 190 },
    'DEMO-D01': { engine: 6.5, idle: 1.5, fuel: 160 },
    'DEMO-D02': { engine: 6.5, idle: 1.25, fuel: 160 },
    'DEMO-D03': { engine: 6, idle: 1.25, fuel: 150 },
    'DEMO-L01': { engine: 6, idle: 1.5, fuel: 125 },
    'DEMO-L02': { engine: 5.5, idle: 1.25, fuel: 115 },
    'DEMO-L03': { engine: 5.5, idle: 1.25, fuel: 110 },
  },
  nonScope: {
    'DEMO-L04': { engine: 5, idle: 1, fuel: 70 },
    'DEMO-S01': { engine: 2, idle: 0.5, fuel: 12 },
  },
  loads: { båt: 0, bil: 2100 },
};

/** Fast testgrunnlag 2. oktober (flaskehals). */
const FIXED_2026_10_02: DaySpec = {
  date: '2026-10-02',
  kind: 'workday',
  tonnes: 3000,
  segments: [
    { state: 'production', fromH: 7, toH: 9.5 },
    { state: 'stop', fromH: 9.5, toH: 11, stopReason: 'Stans: lav tilførsel til grovknuser' },
    { state: 'production', fromH: 11, toH: 12.5 },
    { state: 'stop', fromH: 12.5, toH: 14, stopReason: 'Stans: blokkering i grovknuser' },
    { state: 'production', fromH: 14, toH: 16 },
  ],
  machines: {
    'DEMO-E01': { engine: 7.5, idle: 1.5, fuel: 205 },
    'DEMO-E02': { engine: 7.5, idle: 1.5, fuel: 205 },
    // Tomgang konsentrert i slots som overlapper stoppene 09:30–11:00 og 12:30–14:00
    'DEMO-D01': { engine: 7, idle: 4.5, fuel: 200, idleWeights: [0.4, 0.4, 1, 1, 0.4, 1, 1, 0.4, 0.4] },
    'DEMO-D02': { engine: 7, idle: 1.75, fuel: 175 },
    'DEMO-D03': { engine: 6.5, idle: 1.5, fuel: 165 },
    'DEMO-L01': { engine: 7, idle: 4.5, fuel: 170, idleWeights: [0.4, 0.4, 1, 1, 0.4, 1, 1, 0.4, 0.4] },
    'DEMO-L02': { engine: 5.75, idle: 1.5, fuel: 115 },
    'DEMO-L03': { engine: 5.75, idle: 1.25, fuel: 115 },
  },
  nonScope: {
    'DEMO-L04': { engine: 5.5, idle: 1.25, fuel: 75 },
    'DEMO-S01': { engine: 3, idle: 1, fuel: 18 },
  },
  loads: { båt: 4200, bil: 1800 },
};

/** 3. oktober: planlagt demoskift (lørdag). */
const FIXED_2026_10_03: DaySpec = {
  date: '2026-10-03',
  kind: 'demo_shift',
  note: 'Planlagt ekstraskift',
  tonnes: 3500,
  segments: [
    { state: 'production', fromH: 7, toH: 10.5 },
    { state: 'stop', fromH: 10.5, toH: 11.5, stopReason: 'Planlagt pause' },
    { state: 'production', fromH: 11.5, toH: 15 },
    { state: 'stop', fromH: 15, toH: 16, stopReason: 'Nedkjøring og rengjøring' },
  ],
  machines: {
    'DEMO-E01': { engine: 7, idle: 1.25, fuel: 185 },
    'DEMO-E02': { engine: 7, idle: 1.25, fuel: 185 },
    'DEMO-D01': { engine: 6.5, idle: 1.5, fuel: 155 },
    'DEMO-D02': { engine: 6.5, idle: 1.5, fuel: 150 },
    'DEMO-D03': { engine: 6.5, idle: 1.5, fuel: 150 },
    'DEMO-L01': { engine: 6.5, idle: 1.75, fuel: 125 },
    'DEMO-L02': { engine: 6, idle: 1.5, fuel: 110 },
    'DEMO-L03': { engine: 6, idle: 1.75, fuel: 110 },
  },
  nonScope: {
    'DEMO-L04': { engine: 4.5, idle: 1, fuel: 60 },
    'DEMO-S01': { engine: 1.5, idle: 0.5, fuel: 9 },
  },
  loads: { båt: 0, bil: 1500 },
};

const FIXED: Record<string, DaySpec> = {
  '2026-10-01': FIXED_2026_10_01,
  '2026-10-02': FIXED_2026_10_02,
  '2026-10-03': FIXED_2026_10_03,
};

const TYPE_ENGINE_BASE: Record<string, [number, number]> = {
  gravemaskin: [6.5, 7.5],
  dumper: [6, 7],
  hjullaster: [5.25, 6.5],
  servicebil: [1.5, 3],
};
const TYPE_FUEL_WEIGHT: Record<string, number> = { gravemaskin: 1.6, dumper: 1.35, hjullaster: 1.0, servicebil: 0.1 };

function randomDaySpec(date: string): DaySpec {
  const rnd = mulberry32(hashStr(`${SEED_VERSION}:${date}`));
  const tonnes = roundTo(3600 + rnd() * 800, 10);
  const productionHours = roundTo(7 + rnd() * 1.25, 0.25);
  const stopHours = roundTo(SHIFT_HOURS - productionHours, 0.25);
  const firstProd = roundTo(productionHours * (0.45 + rnd() * 0.1), 0.25);
  const s1 = stopHours > 0.75 ? roundTo(stopHours * 0.6, 0.25) : stopHours;
  const s2 = roundTo(stopHours - s1, 0.25);
  const reasons = ['Planlagt pause og rutinekontroll', 'Stans: tilførsel', 'Stans: sikt', 'Vedlikehold transportbånd', 'Skifte av slitedeler'];
  const segments: Segment[] = [];
  let h = SHIFT_START_HOUR;
  segments.push({ state: 'production', fromH: h, toH: h + firstProd });
  h += firstProd;
  segments.push({ state: 'stop', fromH: h, toH: h + s1, stopReason: reasons[Math.floor(rnd() * reasons.length)] });
  h += s1;
  if (s2 > 0) {
    const p2 = roundTo((productionHours - firstProd) * 0.5, 0.25);
    segments.push({ state: 'production', fromH: h, toH: h + p2 });
    h += p2;
    segments.push({ state: 'stop', fromH: h, toH: h + s2, stopReason: reasons[Math.floor(rnd() * reasons.length)] });
    h += s2;
  }
  segments.push({ state: 'production', fromH: h, toH: SHIFT_START_HOUR + SHIFT_HOURS });

  const idleShare = 0.15 + rnd() * 0.09;
  const lpt = 0.28 + rnd() * 0.05;
  const fuelTotal = roundTo(tonnes * lpt, 1);

  const scope = MACHINES.filter((x) => x.inProductionScope);
  const engines = scope.map((x) => {
    const [lo, hi] = TYPE_ENGINE_BASE[x.type];
    return roundTo(lo + rnd() * (hi - lo), 0.25);
  });
  const engineTotal = engines.reduce((a, b) => a + b, 0);
  const idleTotal = roundTo(engineTotal * idleShare, 0.25);
  const idles = splitExact(idleTotal, engines.map(() => 0.8 + rnd() * 0.4), 2);
  const fuels = splitExact(fuelTotal, scope.map((x, i) => TYPE_FUEL_WEIGHT[x.type] * engines[i]), 1);
  const machines: Record<string, MachineDaySpec> = {};
  scope.forEach((x, i) => {
    machines[x.machineId] = { engine: engines[i], idle: idles[i], fuel: fuels[i] };
  });
  const nonScope: Record<string, MachineDaySpec> = {
    'DEMO-L04': { engine: roundTo(4 + rnd() * 2, 0.25), idle: roundTo(0.75 + rnd() * 0.5, 0.25), fuel: roundTo(55 + rnd() * 25, 1) },
    'DEMO-S01': { engine: roundTo(1 + rnd() * 2, 0.25), idle: roundTo(0.25 + rnd() * 0.5, 0.25), fuel: roundTo(6 + rnd() * 12, 1) },
  };
  const båt = rnd() < 0.3 ? roundTo(3000 + rnd() * 2500, 100) : 0;
  const bil = roundTo(1400 + rnd() * 1200, 100);
  return { date, kind: 'workday', tonnes, segments, machines, nonScope, loads: { båt, bil } };
}

export function dayKindFor(date: string): DayInfo {
  if (FIXED[date]) return { date, kind: FIXED[date].kind, note: FIXED[date].note };
  if (date === '2026-10-04') return { date, kind: 'planned_off', note: 'Planlagt driftsfri (søndag)' };
  const wd = weekdayOf(date);
  if (wd === 0 || wd === 6) return { date, kind: 'planned_off', note: 'Planlagt driftsfri (helg)' };
  return { date, kind: 'workday' };
}

/* ---------- bygging av intervaller ---------- */
function buildDay(spec: DaySpec, scenarioId: string, ingestedAt: string, out: Dataset) {
  const d = spec.date;
  const rateTph = spec.tonnes / spec.segments.filter((s) => s.state === 'production').reduce((a, s) => a + (s.toH - s.fromH), 0);
  let segIdx = 0;
  for (const seg of spec.segments) {
    segIdx++;
    const fromUtc = osloLocalIso(d, Math.floor(seg.fromH), Math.round((seg.fromH % 1) * 60));
    const toUtc = osloLocalIso(d, Math.floor(seg.toH), Math.round((seg.toH % 1) * 60));
    const lsId = `ls-${d}-${segIdx}`;
    out.lineStates.push({
      id: lsId,
      lineId: LINE.lineId,
      fromUtc,
      toUtc,
      state: seg.state,
      stopReason: seg.stopReason,
      sourceId: 'produksjon',
      sourceRecordId: `PLS-STATE-${d}-${segIdx}`,
      revision: 1,
      scenarioId,
    });
    if (seg.state === 'production') {
      const segTonnes = rateTph * (seg.toH - seg.fromH);
      const parts = splitExact(segTonnes, FRACTIONS.map((f) => f.share), 3);
      FRACTIONS.forEach((f, i) => {
        out.production.push({
          id: `prod-${d}-${segIdx}-${f.fractionId}`,
          lineId: LINE.lineId,
          fromUtc,
          toUtc,
          fractionId: f.fractionId,
          producedTonnes: parts[i],
          quality: 'measured',
          sourceId: 'produksjon',
          sourceRecordId: `PLS-PROD-${d}-${segIdx}-${f.fractionId}`,
          revision: 1,
          scenarioId,
        });
      });
    }
  }

  const all: Record<string, MachineDaySpec> = { ...spec.machines, ...spec.nonScope };
  for (const machine of MACHINES) {
    const ms = all[machine.machineId];
    if (!ms) continue;
    const slots = 9;
    const engineParts = splitExact(ms.engine, EVEN, 4);
    const idleParts = splitExact(ms.idle, ms.idleWeights ?? EVEN, 4);
    const fuelParts = splitExact(ms.fuel, engineParts, 3);
    const measuredIdleFuel = machine.type === 'gravemaskin';
    for (let i = 0; i < slots; i++) {
      const fromUtc = osloLocalIso(d, SHIFT_START_HOUR + i, 0);
      const toUtc = osloLocalIso(d, SHIFT_START_HOUR + i + 1, 0);
      const idleFuel = measuredIdleFuel ? Math.round(idleParts[i] * 5.2 * 1000) / 1000 : null;
      out.machineIntervals.push({
        id: `mi-${machine.machineId}-${d}-${String(i + 1).padStart(2, '0')}`,
        machineId: machine.machineId,
        fromUtc,
        toUtc,
        engineHours: engineParts[i],
        idleHours: idleParts[i],
        fuelLitres: fuelParts[i],
        idleFuelLitres: idleFuel,
        quality: 'measured',
        sourceId: 'maskindata',
        sourceRecordId: `TLM-${machine.machineId}-${d}-${String(i + 1).padStart(2, '0')}`,
        revision: 1,
        scenarioId,
        ingestedAtUtc: ingestedAt,
      });
    }
    out.allocations.push({
      machineId: machine.machineId,
      fromUtc: osloLocalIso(d, SHIFT_START_HOUR, 0),
      toUtc: osloLocalIso(d, SHIFT_START_HOUR + SHIFT_HOURS, 0),
      activity: machine.defaultActivity,
      lineId: machine.inProductionScope ? LINE.lineId : null,
      allocationFraction: 1,
      method: 'measured',
    });
  }

  if (spec.loads.bil > 0) {
    out.contextLoads.push({
      id: `load-${d}-bil`,
      date: d,
      fromUtc: osloLocalIso(d, 7, 0),
      toUtc: osloLocalIso(d, 16, 0),
      destination: 'bil',
      tonnes: spec.loads.bil,
      sourceId: 'smartdok',
    });
  }
  if (spec.loads.båt > 0) {
    out.contextLoads.push({
      id: `load-${d}-båt`,
      date: d,
      fromUtc: osloLocalIso(d, 12, 0),
      toUtc: osloLocalIso(d, 18, 0),
      destination: 'båt',
      tonnes: spec.loads.båt,
      sourceId: 'smartdok',
    });
  }
}

function buildSources(scenarioId: string): SourceSync[] {
  const missing = scenarioId === 'manglende-data';
  const machineSucceeded = missing ? osloLocalIso('2026-10-03', 13, 0) : osloLocalIso('2026-10-04', 7, 55);
  return [
    {
      sourceId: 'produksjon',
      name: 'Produksjon (knuseverk PLS)',
      description: 'Produsert mengde per fraksjon og linjetilstand fra knuseverkets styringssystem. Eneste kilde for produsert tonn.',
      mode: 'mock',
      modeLabel: 'Lokal kilde (eksempeldata)',
      status: 'demo',
      attemptedAt: osloLocalIso('2026-10-04', 7, 58),
      succeededAt: osloLocalIso('2026-10-04', 7, 58),
      lastObservedAt: osloLocalIso('2026-10-03', 16, 0),
      expectedCadenceMinutes: 60,
      dataFrom: DEMO_WINDOW.from,
      dataTo: '2026-10-03',
      fields: [
        { field: 'producedTonnes', unit: 't', description: 'Produsert mengde per fraksjon og produksjonsperiode' },
        { field: 'lineState', unit: 'h', description: 'Linjetilstand (produksjon/stopp) med stoppårsak' },
      ],
      isContextOnly: false,
      note: 'I pilot må eksportformat, historikk og API-tilgang fra PLS verifiseres. TeamViewer-tilgang er ikke en datakontrakt.',
    },
    {
      sourceId: 'maskindata',
      name: 'Maskindata (telematikk)',
      description: 'Motortimer, tomgangstimer og dieselforbruk per maskin og timeintervall.',
      mode: 'mock',
      modeLabel: 'Lokal kilde (eksempeldata)',
      status: missing ? 'delayed' : 'demo',
      attemptedAt: osloLocalIso('2026-10-04', 7, 57),
      succeededAt: machineSucceeded,
      lastObservedAt: missing ? osloLocalIso('2026-10-03', 12, 0) : osloLocalIso('2026-10-03', 16, 0),
      expectedCadenceMinutes: 60,
      dataFrom: DEMO_WINDOW.from,
      dataTo: '2026-10-03',
      fields: [
        { field: 'engineHours', unit: 'h', description: 'Motortimer per intervall' },
        { field: 'idleHours', unit: 'h', description: 'Tomgangstimer per intervall (inngår i motortimer)' },
        { field: 'fuelLitres', unit: 'L', description: 'Dieselforbruk per intervall' },
        { field: 'idleFuelLitres', unit: 'L', description: 'Målt tomgangsdrivstoff (kun gravemaskiner)' },
      ],
      isContextOnly: false,
      note: missing
        ? 'Siste vellykkede synk er eldre enn konfigurert frekvens (60 min). Et forventet fuelLitres-intervall for DEMO-D02 mangler 3. oktober.'
        : 'Telematikkdekning varierer per maskin og abonnement. Eksempeldatasettet antar lik dekning.',
    },
    {
      sourceId: 'smartdok',
      name: 'SmartDok (kontekst)',
      description: 'Lasting til båt og bil, maskinoversikt, arbeidstimer. Vises kun som kontekst og legges aldri til produksjonssummen.',
      mode: 'planned',
      modeLabel: 'Planlagt kobling – kontekstdata (lokal)',
      status: 'demo',
      attemptedAt: null,
      succeededAt: null,
      lastObservedAt: osloLocalIso('2026-10-03', 16, 0),
      expectedCadenceMinutes: null,
      dataFrom: DEMO_WINDOW.from,
      dataTo: '2026-10-03',
      fields: [{ field: 'loadedTonnes', unit: 't', description: 'Lastet mengde til båt/bil (kontekst, egen kilde)' }],
      isContextOnly: true,
      note: 'Ingen kobling er etablert. Hva som skal leses inn og hvem som er master for hvert felt må avklares.',
    },
    {
      sourceId: 'okonomi',
      name: 'Økonomi (PowerOffice Go → Tripletex)',
      description: 'Ordre, fakturaer, godkjente timer og lønn. Kun planlagt kobling; ingen data hentes ennå.',
      mode: 'planned',
      modeLabel: 'Planlagt kobling – ingen data',
      status: 'demo',
      attemptedAt: null,
      succeededAt: null,
      lastObservedAt: null,
      expectedCadenceMinutes: null,
      dataFrom: null,
      dataTo: null,
      fields: [],
      isContextOnly: true,
      note: 'Kunden har oppgitt planlagt overgang til Tripletex 1. november. Tidspunkt og omfang må verifiseres før noe kobles til.',
    },
  ];
}

/** Deterministisk scenario-overlegg på samme grunnverden. */
function applyScenarioOverlay(ds: Dataset, scenarioId: string) {
  if (scenarioId === 'manglende-data') {
    // Fjern et forventet fuelLitres-intervall for DEMO-D02 den 3. oktober (slot 10–11).
    const target = ds.machineIntervals.find((x) => x.id === 'mi-DEMO-D02-2026-10-03-04');
    if (target) {
      target.fuelLitres = null;
      target.quality = 'missing';
    }
  }
}

const cache = new Map<string, Dataset>();

export function generateDataset(scenarioIdInput?: string): Dataset {
  const scenario = getScenario(scenarioIdInput);
  const scenarioId = scenario.id;
  const cached = cache.get(scenarioId);
  if (cached) return cached;

  const ds: Dataset = {
    seedVersion: SEED_VERSION,
    scenarioId,
    frozenNow: DEMO_NOW_UTC,
    window: { ...DEMO_WINDOW },
    site: SITE,
    lines: [LINE],
    fractions: FRACTIONS,
    machines: MACHINES,
    days: [],
    production: [],
    lineStates: [],
    machineIntervals: [],
    allocations: [],
    contextLoads: [],
    sources: buildSources(scenarioId),
    rates: DEFAULT_RATES,
  };

  for (const date of eachDay(DEMO_WINDOW.from, DEMO_WINDOW.to)) {
    const info = dayKindFor(date);
    ds.days.push(info);
    if (info.kind === 'planned_off') {
      ds.lineStates.push({
        id: `ls-${date}-off`,
        lineId: LINE.lineId,
        fromUtc: osloLocalIso(date, 0, 0),
        toUtc: osloLocalIso(addDays(date, 1), 0, 0),
        state: 'planned_off',
        sourceId: 'produksjon',
        sourceRecordId: `PLS-CAL-${date}`,
        revision: 1,
        scenarioId,
      });
      continue;
    }
    const spec = FIXED[date] ?? randomDaySpec(date);
    const ingestedAt = osloLocalIso(addDays(date, 1), 2, 0);
    buildDay(spec, scenarioId, ingestedAt, ds);
  }

  applyScenarioOverlay(ds, scenarioId);
  Object.freeze(ds);
  cache.set(scenarioId, ds);
  return ds;
}

/** Lager en ny kopi med utskiftet maskinintervall-liste (til import). Grunnverdenen muteres aldri. */
export function withMachineIntervals(ds: Dataset, machineIntervals: MachineInterval[]): Dataset {
  return { ...ds, machineIntervals };
}

export function withRates(ds: Dataset, rates: Rates): Dataset {
  return { ...ds, rates };
}

export type { ActivityAllocation, LineStateInterval, ProductionInterval, ContextLoad };
